import React from 'react';
import { Box, Chip, Tooltip } from '@mui/material';

import SendIcon from '@mui/icons-material/Send';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined';

import {
  GridActionsCellItem,
  GridColDef,
  GridRenderCellParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid';

import {
  FDataGrid,
  FGridMissingCell,
  FGridMoneyCell,
  FGridPercentCell,
  FGridStatusChip,
  isMissingGridValue,
} from '../grid';

import { ActivityProgrammeRow } from './activityprogramme.models';

interface Props {
  rows: ActivityProgrammeRow[];
  loading?: boolean;

  selectedIds?: number[];

  onSelectionChange?: (ids: number[]) => void;

  onNotifyResponsible?: (row: ActivityProgrammeRow) => void;

  onNotifyIncompleteResponsible?: (row: ActivityProgrammeRow) => void;

  onNotifyAllIncomplete?: () => void;

  onNotifyAllResponsible?: () => void;

  onComplete?: (row: ActivityProgrammeRow) => void;

  onCertify?: (row: ActivityProgrammeRow) => void;

  onReturn?: (row: ActivityProgrammeRow) => void;

  onBulkComplete?: () => void;

  onBulkCertify?: () => void;
}

const QuarterCell = (params: GridRenderCellParams) => (
  <Box
    sx={{
      width: '100%',
      textAlign: 'center',
    }}
  >
    {params.value || ''}
  </Box>
);

export default class ProgrammeReviewGrid extends React.PureComponent<Props> {
  /**
   * Translate the technical issue keys returned by
   * vwx_activityprogramme_review.missing into planner-facing
   * descriptions.
   *
   * IMPORTANT:
   *
   * "cible" does not mean that the numeric target is invalid.
   *
   * A target of zero is legitimate.
   *
   * It means the associated KPIPlan has not yet crossed the
   * planner-confirmation lifecycle boundary:
   *
   *   DRAFT + cible 0      -> requires attention
   *   DRAFT + cible > 0    -> requires attention
   *
   *   PLANNED + cible 0    -> valid
   *   PLANNED + cible > 0  -> valid
   */
  private issueLabel = (issue: string): string => {
    const labels: Record<string, string> = {
      domaine: 'Domaine is not resolved',

      resultat: 'Résultat attendu is not resolved',

      indicateur: 'Indicator is not resolved',

      cible: 'Cible has not been confirmed by the planner',

      responsable: 'Responsible Cost Centre is not resolved',

      tasks: 'No tasks have been planned',
    };

    return labels[issue] ?? issue;
  };

  /**
   * Human-readable multi-line issue description.
   */
  private issueText = (issues: string[]): string => {
    return (issues || []).map((issue) => this.issueLabel(issue)).join('\n');
  };

  /**
   * Number of rows requiring attention.
   *
   * The backend review projection is authoritative:
   *
   *     missing.length > 0
   *
   * means that the Activity cannot currently pass Programme
   * readiness.
   */
  private incompleteCount = () => {
    return (this.props.rows || []).filter(
      (row) => (row.missing ?? []).length > 0
    ).length;
  };

  /**
   * Render the Cible.
   *
   * A visible zero must NOT disappear or be interpreted as an
   * empty value.
   *
   * If "cible" occurs in row.missing, the value exists but has
   * not yet been confirmed by the planner.
   */
  private renderCible = (params: GridRenderCellParams) => {
    const row = params.row as ActivityProgrammeRow;

    const issues = row.missing ?? [];

    const unconfirmed = issues.includes('cible');

    const value = params.value ?? '';

    if (!unconfirmed) {
      return (
        <Box
          sx={{
            width: '100%',
            textAlign: 'right',
          }}
        >
          {value}
        </Box>
      );
    }

    return (
      <Tooltip
        title={
          'Cible has not been confirmed by the planner. ' +
          'A value of zero is valid once the KPI Plan has been confirmed.'
        }
      >
        <Box
          sx={{
            width: '100%',
            textAlign: 'right',
            fontWeight: 700,
            cursor: 'help',
          }}
        >
          {value}
        </Box>
      </Tooltip>
    );
  };

  private columns = (): GridColDef[] => [
    {
      field: 'code',
      headerName: 'Code',
      width: 145,
      renderCell: FGridMissingCell,
      cellClassName: 'programme-cell-master',
    },

    {
      field: 'name',
      headerName: 'Activity',
      minWidth: 280,
      flex: 1,
      renderCell: FGridMissingCell,
      cellClassName: 'programme-cell-master',
    },

    {
      field: 'costcentercode',
      headerName: 'Cost Center',
      width: 140,
      cellClassName: 'programme-cell-governed',
    },

    {
      field: 'domaine',
      headerName: 'Domaine',
      minWidth: 220,
      flex: 1,
      renderCell: FGridMissingCell,
      cellClassName: 'programme-cell-master',
    },

    {
      field: 'resultat',
      headerName: 'Résultat attendu',
      minWidth: 260,
      flex: 1,
      renderCell: FGridMissingCell,
      cellClassName: 'programme-cell-master',
    },

    {
      field: 'indicateur',
      headerName: 'Indicateur',
      minWidth: 250,
      flex: 1,
      renderCell: FGridMissingCell,
      cellClassName: 'programme-cell-master',
    },

    /**
     * CIBLE
     *
     * Do not use truthiness here.
     *
     * 0 is a valid visible target value.
     *
     * Its validity/confirmation is communicated independently
     * through row.missing.
     */
    {
      field: 'cible',
      headerName: 'Cible',
      width: 110,
      type: 'number',
      renderCell: this.renderCible,
      cellClassName: 'programme-cell-governed',
    },

    {
      field: 't1',
      headerName: 'T1',
      width: 65,
      renderCell: QuarterCell,
    },

    {
      field: 't2',
      headerName: 'T2',
      width: 65,
      renderCell: QuarterCell,
    },

    {
      field: 't3',
      headerName: 'T3',
      width: 65,
      renderCell: QuarterCell,
    },

    {
      field: 't4',
      headerName: 'T4',
      width: 65,
      renderCell: QuarterCell,
    },

    {
      field: 'statecode',
      headerName: 'Plan State',
      width: 145,
      renderCell: FGridStatusChip,
      cellClassName: 'programme-cell-governed',
    },

    {
      field: 'taskcount',
      headerName: 'Tasks',
      width: 95,
      type: 'number',
      cellClassName: 'programme-cell-computed',
    },

    {
      field: 'budgetlinecount',
      headerName: 'Budget Lines',
      width: 115,
      type: 'number',
      cellClassName: 'programme-cell-computed',
    },

    {
      field: 'plannedamount',
      headerName: 'Planned Amount',
      width: 155,
      renderCell: FGridMoneyCell,
      cellClassName: 'programme-cell-computed',
    },

    {
      field: 'revisedamount',
      headerName: 'Revised Amount',
      width: 155,
      renderCell: FGridMoneyCell,
      cellClassName: 'programme-cell-computed',
    },

    {
      field: 'variancepercent',
      headerName: 'Écart %',
      width: 110,
      renderCell: FGridPercentCell,
      cellClassName: 'programme-cell-computed',
    },

    /**
     * READINESS
     *
     * Readiness comes from the backend issue projection.
     * It is deliberately independent of ActivityPlan lifecycle.
     */
    {
      field: 'readiness',
      headerName: 'Readiness',
      width: 145,

      valueGetter: (_value, row: ActivityProgrammeRow) => {
        return (row.missing ?? []).length;
      },

      renderCell: (params) => {
        const row = params.row as ActivityProgrammeRow;

        const issues = row.missing ?? [];

        const ready = issues.length === 0;

        const tooltip = ready
          ? 'All mandatory planning information is available.'
          : this.issueText(issues);

        return (
          <Tooltip
            title={
              <span
                style={{
                  whiteSpace: 'pre-line',
                }}
              >
                {tooltip}
              </span>
            }
          >
            <Chip
              size="small"
              color={ready ? 'success' : 'warning'}
              label={ready ? 'Ready' : `${issues.length} issue(s)`}
            />
          </Tooltip>
        );
      },

      cellClassName: 'programme-cell-computed',
    },

    {
      field: 'commentaires',
      headerName: 'Commentaires',
      minWidth: 220,
      flex: 1,
      renderCell: FGridMissingCell,
    },

    {
      field: 'responsable',
      headerName: 'Responsable',
      minWidth: 220,
      flex: 1,
      renderCell: FGridMissingCell,
    },

    /**
     * ACTIONS
     */
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 190,

      getActions: (params) => {
        const row = params.row as ActivityProgrammeRow;

        const issues = row.missing ?? [];

        const incomplete = issues.length > 0;

        const issueText = this.issueText(issues);

        const state = row.statecode;

        return [
          /**
           * General notification.
           */
          <Tooltip key="notify" title="Notify responsible">
            <span>
              <GridActionsCellItem
                icon={<SendIcon fontSize="small" />}
                label="Notify responsible"
                onClick={() => this.props.onNotifyResponsible?.(row)}
              />
            </span>
          </Tooltip>,

          /**
           * Incomplete planning notification.
           */
          <Tooltip
            key="missing"
            title={
              incomplete ? (
                <span
                  style={{
                    whiteSpace: 'pre-line',
                  }}
                >
                  {`Notify responsible of incomplete planning:\n${issueText}`}
                </span>
              ) : (
                'Planning information is complete'
              )
            }
          >
            <span>
              <GridActionsCellItem
                icon={<WarningAmberIcon fontSize="small" />}
                label="Notify incomplete"
                disabled={!incomplete}
                onClick={() => this.props.onNotifyIncompleteResponsible?.(row)}
              />
            </span>
          </Tooltip>,

          /**
           * COMPLETE
           */
          <Tooltip
            key="complete"
            title={
              incomplete ? (
                <span
                  style={{
                    whiteSpace: 'pre-line',
                  }}
                >
                  {`Cannot complete:\n${issueText}`}
                </span>
              ) : state === 'COMPLETE' ? (
                'Activity is already complete'
              ) : state === 'CERTIFIED' ? (
                'Activity is already certified'
              ) : (
                'Mark activity complete'
              )
            }
          >
            <span>
              <GridActionsCellItem
                icon={<CheckCircleOutlineIcon fontSize="small" />}
                label="Mark complete"
                disabled={
                  incomplete || state === 'COMPLETE' || state === 'CERTIFIED'
                }
                onClick={() => this.props.onComplete?.(row)}
              />
            </span>
          </Tooltip>,

          /**
           * CERTIFY
           */
          <Tooltip
            key="certify"
            title={
              incomplete ? (
                <span
                  style={{
                    whiteSpace: 'pre-line',
                  }}
                >
                  {`Cannot certify:\n${issueText}`}
                </span>
              ) : state !== 'COMPLETE' ? (
                'Activity must be completed before certification'
              ) : (
                'Certify activity'
              )
            }
          >
            <span>
              <GridActionsCellItem
                icon={<VerifiedOutlinedIcon fontSize="small" />}
                label="Certify"
                disabled={incomplete || state !== 'COMPLETE'}
                onClick={() => this.props.onCertify?.(row)}
              />
            </span>
          </Tooltip>,

          /**
           * RETURN
           */
          <Tooltip
            key="return"
            title={
              state === 'RETURNED'
                ? 'Activity has already been returned'
                : 'Return activity for revision'
            }
          >
            <span>
              <GridActionsCellItem
                icon={<UndoOutlinedIcon fontSize="small" />}
                label="Return"
                disabled={state === 'RETURNED'}
                onClick={() => this.props.onReturn?.(row)}
              />
            </span>
          </Tooltip>,
        ];
      },
    },
  ];

  private handleSelectionChange = (model: GridRowSelectionModel) => {
    const rows = this.props.rows || [];
    const ids = model.type === 'include'
      ? Array.from(model.ids).map(Number)
      : rows
          .filter((row) => !model.ids.has(row.activityplanid))
          .map((row) => Number(row.activityplanid));

    this.props.onSelectionChange?.(ids);
  };
  private toolbarActions = () => {
    const selected = this.props.selectedIds?.length || 0;

    const incomplete = this.incompleteCount();

    const total = this.props.rows?.length || 0;

    return [
      {
        key: 'complete-selected',

        label: 'Complete Selected',

        icon: <CheckCircleOutlineIcon />,

        disabled: selected === 0,

        onClick: () => this.props.onBulkComplete?.(),
      },

      {
        key: 'certify-selected',

        label: 'Certify Selected',

        icon: <VerifiedOutlinedIcon />,

        disabled: selected === 0,

        onClick: () => this.props.onBulkCertify?.(),
      },

      {
        key: 'notify-incomplete',

        label: 'Notify Incomplete',

        icon: <WarningAmberIcon />,

        disabled: incomplete === 0,

        onClick: () => this.props.onNotifyAllIncomplete?.(),
      },

      {
        key: 'notify-all',

        label: 'Notify All',

        icon: <NotificationsActiveIcon />,

        disabled: total === 0,

        onClick: () => this.props.onNotifyAllResponsible?.(),
      },
    ];
  };

  render() {
    const rows = this.props.rows || [];

    return (
      <FDataGrid
        title={"Programme d'Activités — Horizontal Review"}
        subtitle={
          `${rows.length} Activity Plans • ` +
          `${this.incompleteCount()} incomplete`
        }
        rows={rows}
        columns={this.columns()}
        loading={this.props.loading}
        height={650}
        getRowId={(row: ActivityProgrammeRow) => row.activityplanid}
        checkboxSelection
        disableRowSelectionOnClick
        rowSelectionModel={{
          type: 'include',
          ids: new Set(this.props.selectedIds || []),
        }}
        onRowSelectionModelChange={this.handleSelectionChange}
        disableRowSelectionExcludeModel
        toolbarActions={this.toolbarActions()}
        showQuickFilter
        pageSizeOptions={[25, 50, 100]}
        initialState={{
          pagination: {
            paginationModel: {
              page: 0,
              pageSize: 25,
            },
          },
        }}
        /**
         * There are TWO reasons a cell may need the
         * programme-cell-missing treatment:
         *
         * 1. Its underlying value is physically absent.
         *
         * 2. The backend governance/readiness projection has
         *    explicitly marked the field as requiring attention.
         *
         * The second case is critical for Cible:
         *
         *     cible = 0
         *     KPIPlan = DRAFT
         *
         * Zero is physically present, but the planner has not
         * confirmed it yet.
         */
        getCellClassName={(params) => {
          const row = params.row as ActivityProgrammeRow;

          const issues = row.missing ?? [];

          if (issues.includes(params.field)) {
            return 'programme-cell-missing';
          }

          return isMissingGridValue(row, params.field)
            ? 'programme-cell-missing'
            : '';
        }}
        getRowClassName={(params) => {
          const row = params.row as ActivityProgrammeRow;

          return (row.missing ?? []).length > 0
            ? 'programme-row-incomplete'
            : 'programme-row-ready';
        }}
        sx={{
          '& .programme-cell-master': {
            bgcolor: 'info.lighter',
          },

          '& .programme-cell-computed': {
            bgcolor: 'success.lighter',

            fontWeight: 600,
          },

          '& .programme-cell-governed': {
            bgcolor: 'action.hover',
          },

          '& .programme-cell-missing': {
            bgcolor: 'error.lighter',

            color: 'error.dark',

            fontWeight: 600,
          },

          '& .programme-row-incomplete': {
            boxShadow: 'inset 4px 0 0 0',

            boxShadowColor: 'warning.main',
          },

          '& .programme-row-ready': {
            boxShadow: 'inset 4px 0 0 0',

            boxShadowColor: 'success.main',
          },
        }}
      />
    );
  }
}
